import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { Form as FinalForm } from 'react-final-form';
import { mapFinalFormErrors } from '../lib/utils.js';
import { FormError } from './FormError.jsx';

FormModal.propTypes = {
  children: PropTypes.node.isRequired,
  createResource: PropTypes.func.isRequired,
  deleteResource: PropTypes.func,
  onDeleteSuccess: PropTypes.func,
  onHide: PropTypes.func.isRequired,
  onSubmitSuccess: PropTypes.func,
  resource: PropTypes.object,
  resourceName: PropTypes.string.isRequired,
  show: PropTypes.bool.isRequired,
  updateResource: PropTypes.func,
  validate: PropTypes.func,
};

FormModal.defaultProps = {
  deleteResource: undefined,
  onDeleteSuccess: () => {},
  onSubmitSuccess: () => {},
  resource: undefined,
  validate: undefined,
};

function FormModal({
  children,
  createResource,
  deleteResource,
  onDeleteSuccess,
  onHide,
  onSubmitSuccess,
  resource,
  resourceName,
  show,
  updateResource,
  validate,
  customConfirmMessage,
  getEntities,
  title,
  hideFooter,
  closeButton = true,
  modalSize,
  isInModal,
  className,
}) {
  const editing = !!updateResource && !isEmpty(resource);
  const mapErrors = mapFinalFormErrors(`Failed to save ${resourceName}`);
  const showDelete = !!deleteResource && editing;

  const confirmMessage =
    customConfirmMessage ||
    `Are you sure you want to delete this ${resourceName}?`;

  const [deleting, setDeleting] = useState(false);

  useEffect(() => setDeleting(false), [resource]);

  const onDelete = useCallback(() => {
    if (!window.confirm(confirmMessage)) return;
    setDeleting(true);
    deleteResource().then(onHide).then(onDeleteSuccess);
  }, [deleteResource, onDeleteSuccess, onHide]);
  const onSubmit = useCallback(
    (values) => {
      const submitPromise = editing
        ? updateResource(values)
        : createResource(values);

      return submitPromise
        .then((result, entities) => {
          onHide();
          onSubmitSuccess(result, getEntities ? entities : null);
        })
        .catch(mapErrors);
    },
    [
      createResource,
      editing,
      mapErrors,
      onHide,
      onSubmitSuccess,
      updateResource,
    ]
  );

  return (
    <Modal onHide={onHide} show={show} bsSize={modalSize} className={className}>
      <Modal.Header closeButton={closeButton}>
        <Modal.Title>
          {title ? (
            <span className={!isInModal ? 'text-capitalize' : undefined}>
              {title}
            </span>
          ) : (
            <React.Fragment>
              {editing ? 'Edit' : 'Add'}{' '}
              <span className="text-capitalize">{resourceName}</span>
            </React.Fragment>
          )}
        </Modal.Title>
      </Modal.Header>
      <FinalForm
        initialValues={resource}
        onSubmit={onSubmit}
        validate={validate}
      >
        {({ handleSubmit, submitting, values, submitFailed, submitError }) => {
          const disabled = deleting || submitting;

          return (
            <Form onSubmit={handleSubmit}>
              <Modal.Body>
                {React.Children.map(children, (child) =>
                  React.cloneElement(child, { values })
                )}
                {submitFailed && submitError ? (
                  <FormError submitError={submitError} />
                ) : null}
              </Modal.Body>
              {!hideFooter ? (
                <Modal.Footer>
                  <Row>
                    <Col sm={6}>
                      {showDelete ? (
                        <Button
                          block
                          disabled={disabled}
                          onClick={onDelete}
                          className="btn-danger"
                        >
                          Delete
                        </Button>
                      ) : (
                        <Button block onClick={onHide}>
                          Cancel
                        </Button>
                      )}
                    </Col>
                    <Col sm={6}>
                      <Button
                        block
                        bsStyle="primary"
                        disabled={disabled}
                        type="submit"
                      >
                        Save
                      </Button>
                    </Col>
                  </Row>
                </Modal.Footer>
              ) : null}
            </Form>
          );
        }}
      </FinalForm>
    </Modal>
  );
}

export default FormModal;
